import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, Pause, Play } from "lucide-react";
import { api, downloadFile, errorMessage, fileUrl, Query } from "../api/client";
import type { Inspection, Operator, Paged, Part } from "../api/types";
import { useSession } from "../store/session";
import { Button, Input, Select, Toggle } from "../components/Controls";
import { AiBadge, DemoTag, VerdictBadge } from "../components/Badge";
import { Column, Table } from "../components/Table";
import { DateRange } from "../components/DateRange";
import { EmptyState } from "../components/EmptyState";
import { toast } from "../components/Toast";
import { addDays, endIso, fmtNum, fmtTime, localDateStr, rangeIncludesToday, shortId, startIso } from "../lib/format";

export const NAV_KEY = "insp.nav";

function isDiscrepancy(i: Inspection) {
  return !!i.ai_result && !!i.operator_verdict && i.ai_result !== i.operator_verdict;
}

function pointOptions(parts: Part[], partId: string | null): number[] {
  const sel = parts.find((p) => p.id === partId);
  const n = sel ? sel.points.length : Math.max(4, ...parts.map((p) => p.points?.length || 0));
  return Array.from({ length: n }, (_, i) => i);
}

const FILTER_KEYS = ["operator_id", "part_id", "ai_result", "operator_verdict", "point_index", "device_id", "sample_id"];

export function InspectionsView({ fixedPartId }: { fixedPartId?: string }) {
  const { session } = useSession();
  const isAdmin = session.operator.role === "admin";
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const today = localDateStr();
  const from = sp.has("from") ? sp.get("from") : addDays(today, -6);
  const to = sp.has("to") ? sp.get("to") : today;
  const page = Number(sp.get("page") || 1);
  const origin = sp.get("origin") || "all";

  const [data, setData] = useState<Paged<Inspection> | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [paused, setPaused] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [parts, setParts] = useState<Part[]>([]);

  const set = (patch: Record<string, string | null>) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v === null ? n.delete(k) : n.set(k, v)));
    if (!("page" in patch)) n.delete("page");
    setSp(n, { replace: true });
  };

  const query: Query = useMemo(() => {
    const q: Query = { from: startIso(from), to: endIso(to) };
    FILTER_KEYS.forEach((k) => {
      if (sp.get(k)) q[k] = sp.get(k);
    });
    if (sp.get("only_discrepancies") === "true") q.only_discrepancies = "true";
    if (origin === "demo") q.is_demo = "true";
    if (origin === "real") q.is_demo = "false";
    if (fixedPartId) q.part_id = fixedPartId;
    return q;
  }, [sp, from, to, origin, fixedPartId]);

  const load = useCallback(async () => {
    try {
      const r = await api.get<Paged<Inspection>>("/inspections", { ...query, page, limit: 50 });
      setData(r);
      setUpdatedAt(Date.now());
      sessionStorage.setItem(NAV_KEY, JSON.stringify({ ids: r.items.map((i) => i.id), back: window.location.pathname + window.location.search }));
    } catch {
      // toast shown by client on network errors
    } finally {
      setLoading(false);
    }
  }, [query, page]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const live = rangeIncludesToday(from, to) && !paused;
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [live, load]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    api.get<Operator[]>("/operators", { active: "all" }).then(setOperators).catch(() => undefined);
    api.get<Part[]>("/parts", { active: "all" }).then(setParts).catch(() => undefined);
  }, [isAdmin]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadFile("/inspections/export.csv", query, "inspecciones.csv");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const columns: Column<Inspection>[] = [
    { key: "time", header: "Hora", render: (i) => <span className="font-mono">{fmtTime(i.timestamp)}</span> },
    { key: "op", header: "Operario", render: (i) => <span className="inline-flex items-center gap-1.5">{i.operator?.name || "—"}{i.is_demo && <DemoTag />}</span> },
    { key: "part", header: "Pieza", render: (i) => <span className="font-mono">{i.part?.part_number || "—"}</span> },
    { key: "pt", header: "Punto", render: (i) => <span title={i.point_name || ""}>{i.point_index + 1}</span> },
    { key: "img", header: "Foto", hideOnMobile: true, render: (i) => (i.thumb_url ? <img src={fileUrl(i.thumb_url)} alt="" className="h-12 w-12 rounded object-cover" loading="lazy" /> : <span className="text-slate-400">—</span>) },
    { key: "ai", header: "IA", render: (i) => <AiBadge result={i.ai_result} score={i.ai_score} testId="inspection-ai-badge" /> },
    { key: "verdict", header: "Veredicto", render: (i) => <VerdictBadge value={i.operator_verdict} testId="inspection-verdict-badge" /> },
    { key: "vs", header: "T. veredicto", render: (i) => <span className="font-mono">{i.verdict_seconds === null ? "—" : `${fmtNum(i.verdict_seconds, 1)} s`}</span> },
    { key: "lat", header: "Latencia", render: (i) => <span className="font-mono">{i.latency_ms === null ? "—" : `${i.latency_ms} ms`}</span> },
    { key: "dev", header: "Dispositivo", render: (i) => <span className="font-mono text-xs" title={i.device_id}>{shortId(i.device_id, 10)}</span> },
  ];

  const ago = updatedAt ? Math.max(0, Math.round((now - updatedAt) / 1000)) : null;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="inspections-filters">
        <div className="sm:col-span-2"><DateRange from={from} to={to} onChange={(f, t) => set({ from: f, to: t })} /></div>
        {isAdmin && (
          <Select value={sp.get("operator_id") || ""} onChange={(e) => set({ operator_id: e.target.value || null })} data-testid="filter-operator-select">
            <option value="">Todos los operarios</option>
            {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </Select>
        )}
        {isAdmin && !fixedPartId && (
          <Select value={sp.get("part_id") || ""} onChange={(e) => set({ part_id: e.target.value || null })} data-testid="filter-part-select">
            <option value="">Todas las piezas</option>
            {parts.map((p) => <option key={p.id} value={p.id}>{p.part_number} · {p.name}</option>)}
          </Select>
        )}
        <Select value={sp.get("ai_result") || ""} onChange={(e) => set({ ai_result: e.target.value || null })} data-testid="filter-ai-result-select">
          <option value="">Resultado IA: todos</option>
          <option value="OK">IA: OK</option>
          <option value="NG">IA: NG</option>
        </Select>
        <Select value={sp.get("operator_verdict") || ""} onChange={(e) => set({ operator_verdict: e.target.value || null })} data-testid="filter-verdict-select">
          <option value="">Veredicto: todos</option>
          <option value="OK">Veredicto: OK</option>
          <option value="NG">Veredicto: NG</option>
        </Select>
        <Select value={sp.get("point_index") || ""} onChange={(e) => set({ point_index: e.target.value || null })} data-testid="filter-point-select">
          <option value="">Todos los puntos</option>
          {pointOptions(parts, sp.get("part_id")).map((i) => <option key={i} value={i}>{`Punto ${i + 1}`}</option>)}
        </Select>
        <Select value={origin} onChange={(e) => set({ origin: e.target.value === "all" ? null : e.target.value })} data-testid="filter-origin-select">
          <option value="all">Origen: todas</option>
          <option value="demo">Solo demo</option>
          <option value="real">Solo reales</option>
        </Select>
        <Input placeholder="Dispositivo" defaultValue={sp.get("device_id") || ""} onBlur={(e) => set({ device_id: e.target.value.trim() || null })} data-testid="filter-device-input" />
        <Input placeholder="Id de muestra" defaultValue={sp.get("sample_id") || ""} onBlur={(e) => set({ sample_id: e.target.value.trim() || null })} data-testid="filter-sample-input" />
        <div className="flex items-center"><Toggle checked={sp.get("only_discrepancies") === "true"} onChange={(v) => set({ only_discrepancies: v ? "true" : null })} label="Solo discrepancias" testId="filter-discrepancies-toggle" /></div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-500">
        <div className="flex items-center gap-2" data-testid="inspections-refresh-indicator">
          {rangeIncludesToday(from, to) ? (
            <>
              <span className={paused ? "h-2 w-2 rounded-full bg-slate-300" : "h-2 w-2 animate-pulse rounded-full bg-ok"} />
              {ago !== null && <span>Actualizado hace {ago} s</span>}
              <button className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-slate-100" onClick={() => setPaused(!paused)} data-testid="inspections-pause-button">
                {paused ? <><Play className="h-3.5 w-3.5" /> Reanudar</> : <><Pause className="h-3.5 w-3.5" /> Pausar</>}
              </button>
            </>
          ) : <span>Rango sin hoy: sin auto-actualización</span>}
        </div>
        {isAdmin && <Button variant="secondary" size="sm" onClick={exportCsv} loading={exporting} data-testid="export-csv-button"><Download className="h-3.5 w-3.5" /> Exportar CSV</Button>}
      </div>
      <Table
        testId="inspections-table"
        columns={columns}
        rows={data?.items || []}
        loading={loading}
        total={data?.total}
        page={page}
        limit={50}
        onPage={(p) => set({ page: String(p) })}
        onRowClick={(i) => navigate(`/inspecciones/${i.id}`)}
        rowClassName={(i) => (i.repeated ? "text-slate-400 line-through opacity-70" : isDiscrepancy(i) ? "bg-warn-soft hover:bg-warn-soft/70" : "")}
        empty={<EmptyState title={from === today && to === today ? "Todavía no hay inspecciones hoy" : "No hay inspecciones con estos filtros"} text="Las inspecciones nuevas aparecen solas cada 10 segundos." testId="inspections-empty" />}
      />
    </div>
  );
}
